/**
 * humanize-audit-action.ts — surowy kod akcji audytu → czytelna polska etykieta.
 *
 * Kody akcji mają wzorzec `encja.czasownik` (czasem `encja.pod.czasownik`), np.
 * `tenant.suspend`, `contractor.created`, `admin.login.success`. W logu (zakładka
 * „Audit" tenanta oraz „Log zdarzeń") pokazywaliśmy je surowo (mono) — zbiór jest
 * OTWARTY (kilkaset kodów), więc zamiast słownika pełnych kodów tłumaczymy
 * heurystycznie: OSTATNI segment = czasownik, wcześniejsze = encja; każdy segment
 * mapujemy na polskie słowo, resztę składamy „Encja · czasownik".
 *
 * Słownictwo (czasowniki/encje) jest podawane jako `defaultValue` do `t()` — dzięki
 * temu przechodzi przez i18n (można nadpisać kluczem `audit.verb.*` / `audit.entity.*`
 * w pl.json/en.json), a jednocześnie nie trzeba utrzymywać setek kluczy dla
 * otwartego zbioru. Segment bez tłumaczenia → prettify (podkreślenia na spacje).
 */

import type { TFunction } from 'i18next';

/** Polskie czasowniki akcji (bezosobowo, jak w logu: „utworzono", „zawieszono"). */
const VERB_PL: Record<string, string> = {
  created: 'utworzono',
  create: 'utworzono',
  opened: 'otwarto',
  queried: 'wykonano zapytanie',
  updated: 'zaktualizowano',
  update: 'zaktualizowano',
  deleted: 'usunięto',
  delete: 'usunięto',
  removed: 'usunięto',
  soft_deleted: 'usunięto (kosz)',
  soft_delete: 'usunięto (kosz)',
  hard_deleted: 'usunięto trwale',
  restored: 'przywrócono',
  restore: 'przywrócono',
  published: 'opublikowano',
  uploaded: 'wgrano',
  edit: 'edytowano',
  reset: 'zresetowano',
  added: 'dodano',
  assigned: 'przypisano',
  revoked: 'cofnięto',
  replaced: 'zastąpiono',
  rejected: 'odrzucono',
  withdrawn: 'wycofano',
  saved: 'zapisano',
  upserted: 'zapisano',
  cleared: 'wyczyszczono',
  cancelled: 'anulowano',
  disabled: 'wyłączono',
  enabled: 'włączono',
  activate: 'aktywowano',
  deactivate: 'dezaktywowano',
  invited: 'zaproszono',
  accepted_invite: 'przyjęto zaproszenie',
  duplicated: 'zduplikowano',
  verified: 'zweryfikowano',
  verify_failed: 'weryfikacja nieudana',
  unlocked: 'odblokowano',
  locked: 'zablokowano',
  success: 'sukces',
  failed: 'niepowodzenie',
  identifier_changed: 'zmieniono identyfikator',
  forgot_requested: 'zażądano resetu hasła',
  trial_extended: 'przedłużono trial',
  item_created: 'dodano pozycję',
  item_updated: 'zmieniono pozycję',
  item_deleted: 'usunięto pozycję',
  sql_tested: 'przetestowano SQL',
  playground_reset: 'zresetowano playground',
  sent: 'wysłano',
  suspend: 'zawieszono',
  suspended: 'zawieszono',
  shared: 'udostępniono',
  unshared: 'cofnięto udostępnienie',
  pinned: 'przypięto',
  unpinned: 'odpięto',
  upgrade_seats: 'zmieniono liczbę stanowisk',
};

/** Polskie nazwy encji (pierwsze segmenty kodu). */
const ENTITY_PL: Record<string, string> = {
  tenant: 'Klient',
  tenant_db: 'Baza tenanta',
  processes: 'Procesy',
  admin: 'Administrator',
  crm_core: 'CRM',
  auth: 'Uwierzytelnianie',
  login: 'Logowanie',
  '2fa': '2FA',
  erp_integration: 'Integracja ERP',
  contractor: 'Kontrahent',
  user: 'Użytkownik',
  global_dictionary: 'Słownik globalny',
  custom_module: 'Moduł własny',
  module_template: 'Szablon modułu',
  module: 'Moduł',
  company: 'Firma',
  chart_of_accounts_template: 'Szablon planu kont',
  chart_of_account: 'Plan kont',
  system_kartoteka: 'Kartoteka systemowa',
  global_document_type: 'Globalny typ dokumentu',
  contract: 'Umowa',
  warehouse: 'Magazyn',
  subscription: 'Subskrypcja',
  sale_invoice: 'Faktura sprzedaży',
  purchase_invoice: 'Faktura zakupu',
  invoice: 'Faktura',
  record_link: 'Powiązanie',
  record_link_type: 'Typ powiązania',
  menu_layout: 'Układ menu',
  admin_menu_layout: 'Układ menu admina',
  letter: 'Pismo',
  ksef_invoice: 'Faktura KSeF',
  ksef_connection: 'Połączenie KSeF',
  ksef_automation_rule: 'Reguła automatyzacji KSeF',
  internal: 'Dokument wewnętrzny',
  commercial: 'Dokument handlowy',
  specjalne: 'Dokument specjalny',
  employee: 'Pracownik',
  department: 'Dział',
  decree_template: 'Szablon dekretu',
  decree: 'Dekretacja',
  bundle: 'Pakiet',
  coupon: 'Kupon',
  attachment: 'Załącznik',
  admin_role: 'Rola administratora',
  system_module_layout: 'Układ modułu',
  system_module_field: 'Pole modułu',
};

/** Prettify segmentu bez tłumaczenia: podkreślenia → spacje. */
function prettifySegment(seg: string): string {
  return seg.replace(/_/g, ' ');
}

export interface HumanizedAction {
  /** Czytelna etykieta „Encja · czasownik" (z wielkiej litery). */
  label: string;
  /** Surowy kod akcji — do pokazania w tooltipie / subtekście. */
  raw: string;
}

/**
 * Zamienia surowy kod akcji audytu na czytelną polską etykietę.
 * `t` służy do ewentualnego nadpisania słownika przez i18n; gdy klucza brak,
 * używany jest wbudowany słownik PL (jako `defaultValue`), a dla nieznanych
 * segmentów — prettify.
 */
export function humanizeAuditAction(action: string, t: TFunction): HumanizedAction {
  const raw = action ?? '';
  const segments = raw.split('.').filter(Boolean);
  if (segments.length === 0) return { label: raw, raw };

  const verb = segments[segments.length - 1]!;
  const entitySegs = segments.slice(0, -1);

  const verbLabel = t(`audit.verb.${verb}`, {
    defaultValue: VERB_PL[verb] ?? prettifySegment(verb),
  });
  const entityLabel = entitySegs
    .map((s) => t(`audit.entity.${s}`, { defaultValue: ENTITY_PL[s] ?? prettifySegment(s) }))
    .join(' · ');

  const combined = entityLabel ? `${entityLabel} · ${verbLabel}` : String(verbLabel);
  return { label: combined.charAt(0).toUpperCase() + combined.slice(1), raw };
}
